import type { AssignmentListItem } from '@repo/types/assignments';

import { assignmentErrorMessages } from '@repo/lib/messages/assignmentErrorMessages';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useErrorMessage } from '#error-message/useErrorMessage';

import type { DeleteAssignmentInput } from './assignmentService';

import { AssignmentService } from './assignmentService';
import { invalidateAssignedAssignments } from './invalidateAssignedAssignments';
import { AssignmentKey } from './keys';

export function useDeleteAssignment() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (input: DeleteAssignmentInput) => AssignmentService.deleteAssignment(input).then((response) => response.data),
    onMutate: async (input: DeleteAssignmentInput) => {
      await queryClient.cancelQueries({
        queryKey: [AssignmentKey.ASSIGNED_ASSIGNMENTS],
      });
      const previous = queryClient.getQueriesData<AssignmentListItem[]>({
        queryKey: [AssignmentKey.ASSIGNED_ASSIGNMENTS],
      });
      queryClient.setQueriesData<AssignmentListItem[]>({ queryKey: [AssignmentKey.ASSIGNED_ASSIGNMENTS] }, (prev) => (prev ? prev.filter((a) => a.id !== input.id) : prev));
      return { previous };
    },
    onError: (_error, _input, context) => {
      context?.previous.forEach(([key, value]) => {
        queryClient.setQueryData(key, value);
      });
    },
    onSettled: () => invalidateAssignedAssignments(queryClient),
  });

  const errorMessage = useErrorMessage(mutation.error, assignmentErrorMessages);

  return { ...mutation, errorMessage };
}
